import { Constants, getGameMode } from "@nextgammon/shared";
import { useEffect, useRef, useState } from "react";

interface TurnTimerProps {
  turnDeadline: number | null; // Absolute timestamp when turn expires
  turnStartedAt: number | null; // Absolute timestamp when turn started
  currentTurn: string | null;
  currentUserId: string;
  serverTimeRemaining?: number | null; // Server-calculated time remaining
  gameMode?: string;
  timeBank1?: number | null;
  timeBank2?: number | null;
  player1Id?: string;
  player2Id?: string;
}

export function TurnTimer({
  turnDeadline,
  turnStartedAt,
  currentTurn,
  currentUserId,
  serverTimeRemaining,
  gameMode = "standard",
  timeBank1,
  timeBank2,
  player1Id,
  player2Id
}: TurnTimerProps) {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  // Refs to hold latest server values for interval access without restarts
  const serverTimeRemainingRef = useRef<number | null>(serverTimeRemaining || null);
  const lastServerUpdateRef = useRef<number>(Date.now());

  // Update refs and state when server values or turn changes
  useEffect(() => {
    // Update refs
    serverTimeRemainingRef.current = serverTimeRemaining || null;
    lastServerUpdateRef.current = Date.now();

    // Update state
    if (!currentTurn || serverTimeRemaining === null || serverTimeRemaining === undefined) {
      setTimeRemaining(null);
      return;
    }

    // Use server's time remaining as the starting point
    setTimeRemaining(serverTimeRemaining);
  }, [serverTimeRemaining, currentTurn]);

  // Count down locally between server updates
  // Only restart interval on turn changes, not on every server update
  useEffect(() => {
    if (!currentTurn) {
      return;
    }

    // Update timer based on elapsed time since last server update
    const updateTimer = () => {
      const serverTime = serverTimeRemainingRef.current;
      const lastUpdate = lastServerUpdateRef.current;

      if (serverTime === null || !lastUpdate) return;

      const now = Date.now();
      const elapsed = now - lastUpdate;
      const remaining = Math.max(0, serverTime - elapsed);
      setTimeRemaining(remaining);

      // Auto-clear interval when timer expires
      if (remaining === 0) {
        clearInterval(interval);
      }
    };

    // Start the timer immediately
    updateTimer();

    // Update every 1000ms for smoother countdown
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [currentTurn]); // Only depends on currentTurn - restarts on turn change only

  // Don't show timer if no turn is active
  if (!currentTurn || timeRemaining === null) {
    return null;
  }

  const isMyTurn = currentTurn === currentUserId;

  // Get game mode configuration
  const mode = getGameMode(gameMode);
  const isTimeBankMode = mode.totalTimeLimit > 0;

  // Get current player's time bank - be defensive about data consistency
  let currentPlayerTimeBank: number | null = null;

  // Only select time bank if we have all required data
  if (currentTurn && player1Id && player2Id) {
    if (currentTurn === player1Id && timeBank1 !== undefined) {
      currentPlayerTimeBank = timeBank1;
    } else if (currentTurn === player2Id && timeBank2 !== undefined) {
      currentPlayerTimeBank = timeBank2;
    }
  }

  // Calculate turn duration and determine what phase we're in
  let turnDuration: number;
  let isInBronstein = false;
  let isConsumingBank = false;

  if (isTimeBankMode && currentPlayerTimeBank !== null && currentPlayerTimeBank !== undefined) {
    // Time bank mode - show Bronstein + bank
    const timeSinceTurnStart = turnStartedAt ? Date.now() - turnStartedAt : 0;
    isInBronstein = timeSinceTurnStart < mode.bronstein;
    isConsumingBank =
      !isInBronstein && timeRemaining !== null && timeRemaining <= currentPlayerTimeBank;

    if (isInBronstein) {
      // During Bronstein delay
      turnDuration = mode.bronstein;
    } else {
      // After Bronstein, using time bank
      turnDuration = currentPlayerTimeBank + mode.bronstein;
    }
  } else {
    // Standard mode or no time bank
    turnDuration =
      turnStartedAt && turnDeadline ? turnDeadline - turnStartedAt : Constants.TURN_TIMEOUT_MS;
  }

  // Calculate percentages for both bars in Speedgammon mode
  let bronsteinPercentage = 0;
  let bankPercentage = 0;
  let singleBarPercentage = 0;

  if (isTimeBankMode) {
    // Speedgammon mode - dual bars
    const timeSinceTurnStart = turnStartedAt ? Date.now() - turnStartedAt : 0;

    if (isInBronstein) {
      // Show Bronstein countdown (remaining time as percentage)
      const remainingBronstein = Math.max(0, mode.bronstein - timeSinceTurnStart);
      bronsteinPercentage = Math.max(0, (remainingBronstein / mode.bronstein) * 100);
    }

    // For time bank: Show actual remaining time as percentage
    if (isInBronstein) {
      // During Bronstein, show full bank
      if (currentPlayerTimeBank !== null && currentPlayerTimeBank !== undefined) {
        bankPercentage = Math.max(0, (currentPlayerTimeBank / mode.totalTimeLimit) * 100);
      }
    } else if (timeRemaining !== null) {
      // After Bronstein, use timeRemaining which represents actual bank remaining
      bankPercentage = Math.max(0, (timeRemaining / mode.totalTimeLimit) * 100);
    }
  } else {
    // Standard mode - single bar
    const totalDuration =
      turnStartedAt && turnDeadline ? turnDeadline - turnStartedAt : Constants.TURN_TIMEOUT_MS;
    singleBarPercentage = Math.max(0, (timeRemaining / totalDuration) * 100);
  }

  // Standard mode color logic
  const getStandardBarColor = () => {
    if (timeRemaining < 10000) return "bg-red-500"; // Less than 10 seconds
    if (timeRemaining < 30000) return "bg-yellow-500"; // Less than 30 seconds
    return "bg-green-500"; // More than 30 seconds
  };

  // Helper function to format time display
  const formatTime = (ms: number) => {
    const seconds = Math.ceil(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  // For Speedgammon, show appropriate time based on phase
  const getDisplayTime = () => {
    if (!isTimeBankMode || timeRemaining === null) {
      return formatTime(timeRemaining || 0);
    }

    // In time bank mode, server's timeRemaining calculation:
    // - During Bronstein: (bronstein_remaining + full_bank)
    // - After Bronstein: (bank - time_consumed_after_bronstein)
    if (turnStartedAt) {
      const timeSinceTurnStart = Date.now() - turnStartedAt;

      if (timeSinceTurnStart < mode.bronstein) {
        // During Bronstein: Show only the Bronstein countdown
        const remainingBronstein = Math.max(0, mode.bronstein - timeSinceTurnStart);
        return formatTime(remainingBronstein);
      } else {
        // After Bronstein: timeRemaining from server is already correct
        // It's the bank minus time consumed after Bronstein
        return formatTime(timeRemaining);
      }
    }

    return formatTime(timeRemaining);
  };

  return (
    <div
      className={`fixed left-40 z-50 flex items-center gap-3 px-4 py-1 rounded-lg backdrop-blur-sm ${
        isMyTurn ? "bg-slate-900/50 bottom-2" : "bg-gray-800/50 top-2"
      }`}
    >
      {/* Progress bar container */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <svg
            className={`w-4 h-4 ${
              timeRemaining < 10000
                ? "text-red-400 animate-pulse"
                : timeRemaining < 30000
                  ? "text-yellow-400"
                  : "text-green-400"
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {isMyTurn && (
            <span className="text-xs text-white/90 font-medium">
              {isTimeBankMode && isInBronstein
                ? "Free time"
                : isTimeBankMode && isConsumingBank
                  ? "Time bank"
                  : isTimeBankMode
                    ? "Speedgammon"
                    : "Your turn"}
            </span>
          )}
        </div>

        {/* Progress bars - dual bars for Speedgammon, single for Standard */}
        {isTimeBankMode ? (
          /* Speedgammon mode - stacked bars */
          <div className="flex flex-col gap-1">
            {/* Bronstein free time bar - always rendered, visible only when active */}
            <div className="w-32 h-2 bg-gray-700/50 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-1000 ease-linear shadow-sm ${
                  isInBronstein
                    ? "bg-green-500 opacity-100 shadow-green-500/50"
                    : "bg-green-500 opacity-0"
                }`}
                style={{ width: `${bronsteinPercentage}%` }}
              />
            </div>

            {/* Time bank bar - always visible, shows actual remaining percentage */}
            <div className="w-32 h-2 bg-gray-700/50 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-1000 ease-linear shadow-sm ${
                  isConsumingBank
                    ? "bg-orange-500 shadow-orange-500/50"
                    : "bg-blue-500 shadow-blue-500/50"
                }`}
                style={{ width: `${bankPercentage}%` }}
              />
            </div>
          </div>
        ) : (
          /* Standard mode - single bar */
          <div className="w-32 h-2 bg-gray-700/50 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-1000 ease-linear ${getStandardBarColor()} shadow-sm`}
              style={{ width: `${singleBarPercentage}%` }}
            />
          </div>
        )}
      </div>

      {/* Time display */}
      <span
        className={`text-sm font-mono ${timeRemaining < 10000 ? "text-red-400" : "text-white/90"}`}
      >
        {getDisplayTime()}
      </span>
    </div>
  );
}
